import { useState, useEffect } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { getDistance } from '../utils/store-locator';
import { themeI18n } from '../utils/i18n_helper';
import { Drawer } from './Drawer.js';
import { StoreDrawerLocation } from './StoreDrawerLocation.js';
import { multilingual_url } from '../utils/multilingual-url';

import Icons from './Icons.js';

const userLocation = JSON.parse(localStorage.getItem('theme:user_location'));
const storeLocationsData = JSON.parse(document.querySelector('[data-store-locations-data]').innerHTML).locations;

export const StoresDrawer = ({ drawerName, myStoreLocationSinceDrawerOpened, myStoreLocation, setMyStoreLocation, locationInventories }) => {
  const [storeLocations, setStoreLocations] = useState(storeLocationsData); // consider using GraphQL to fetch store locations if many locations
  const [userAddress, setUserAddress] = useState('');
  const [userLatLng, setUserLatLng] = useState({ lat: userLocation?.lat, lng: userLocation?.lng });
  const [userLocationName, setUserLocationName] = useState(localStorage.getItem('theme:user_location_name'));
  const [showOnlyStoresWithInventory, setShowOnlyStoresWithInventory] = useState(false);

  const geocodingLibrary = useMapsLibrary('geocoding');
  const geocodeAddress = () => {
    const geocoder = new geocodingLibrary.Geocoder();

    geocoder.geocode({ address: userAddress }, (results, status) => {
      if (status === 'OK') {
        setUserLatLng({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });

        if (window.toast) {
          window.toast.success({
            message: themeI18n('store_locator.address_success')
          });
        }
      } else {
        if (window.toast) {
          window.toast.error({
            message: themeI18n('store_locator.address_error')
          });
        }
      }
    });
  }

  const handleMyStoreUpdate = (event) => {
    setMyStoreLocation(event.detail);
  }

  const sortStoresByDistance = () => {
    const updatedStores = storeLocations.map(store => {
      const distance = getDistance(userLatLng.lat, parseFloat(store.latitude), userLatLng.lng,  parseFloat(store.longitude));
      return {
        ...store,
        distance,
      };
    });

    setStoreLocations(updatedStores.sort((a, b) => a.distance - b.distance));
  }

  // sort stores by distance when user location is updated
  useEffect(() => {
    if (storeLocations && userLatLng.lat && userLatLng.lng) {
      sortStoresByDistance();
      localStorage.setItem('theme:user_location', JSON.stringify(userLatLng));

      if (userAddress) {
        localStorage.setItem('theme:user_location_name', userAddress);
        setUserLocationName(userAddress);
      }
    }
  }, [userLatLng]);

  // filter stores by inventory
  useEffect(() => {
    let updatedStores = storeLocationsData;

    if (showOnlyStoresWithInventory && locationInventories) {

      updatedStores = updatedStores.filter(store => {
        const locationInventoryData = locationInventories.find(location => location.location_id === store.shopify_id);

        return locationInventoryData.available;
      });
    }

    if (userLatLng.lat && userLatLng.lng) {
      updatedStores = updatedStores.map(store => {
        const distance = getDistance(userLatLng.lat, parseFloat(store.latitude), userLatLng.lng,  parseFloat(store.longitude));
        return {
          ...store,
          distance,
        };
      });

      updatedStores.sort((a, b) => a.distance - b.distance);
    }

    setStoreLocations(updatedStores);
  }, [showOnlyStoresWithInventory, locationInventories]);

  useEffect(() => {
    window.addEventListener('myStoreChanged', (event) => handleMyStoreUpdate(event));
    window.addEventListener('open_locations_drawer' + drawerName, () => {
      if (!userLatLng.lat && !userLatLng.lng && 'geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition((position) => {
          setUserLatLng({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });

          localStorage.setItem('theme:user_location_name', themeI18n('store_locator.detected_position'));
          setUserLocationName(themeI18n('store_locator.detected_position'));

          if (window.toast) {
            window.toast.success({
              message: themeI18n('store_locator.address_success')
            });
          }
        });
      }
    });

    if (storeLocations && userLatLng.lat && userLatLng.lng) {
      sortStoresByDistance();
    }

    return () => {
      window.removeEventListener('myStoreChanged', (event) => handleMyStoreUpdate(event));
    }
  },[]);

  return (
    <Drawer openEvent={"open_locations_drawer" + drawerName} direction="right" headerContent={themeI18n('store_locator.select_a_store')} contentClass="locations-drawer__drawer-content">
      <div className='locations-drawer__content'>
        <div className='locations-drawer__content-upper'>
          <RenderAddressForm geocodeAddress={geocodeAddress} userAddress={userAddress} setUserAddress={setUserAddress}/>

          {(userLatLng.lat && userLocationName) && (
            <p className='locations-drawer__stores-sorter-disclaimer'>
              {themeI18n('store_locator.stores_are_sorted_by_distance_to')} <strong>{userLocationName}</strong>
            </p>
          )}

          {locationInventories && (
            <label className='locations-drawer__show-only-stores-with-inventory'>
              <input type='checkbox' className='visually-hidden' checked={showOnlyStoresWithInventory} onChange={()=> {
                setShowOnlyStoresWithInventory(!showOnlyStoresWithInventory);
              }}/>
              {showOnlyStoresWithInventory ? <Icons icon='checkbox-checked' size='small' /> : <Icons icon='checkbox' size='small' />}
              {themeI18n('store_locator.show_only_stores_with_inventory')}
            </label>
          )}
        </div>

        <div className='locations-drawer__locations'>
          {storeLocations.map(store => {
            const storeInventory = locationInventories ? locationInventories.find(location => location.location_id === store.shopify_id) : null;

            return (
              <StoreDrawerLocation key={store.id} store={store} myStoreLocation={myStoreLocation} first={store.id == myStoreLocationSinceDrawerOpened} storeInventory={storeInventory}/>
            )
          })}
        </div>
      </div>
    </Drawer>
  )
}

const RenderAddressForm = ({ className, userAddress, setUserAddress, geocodeAddress }) => (
  <form className={`locations-drawer__form ${className}`} onSubmit={(event)=> {
    event.preventDefault();

    geocodeAddress(event);
  }}>
    <input type="text" id="postal-code" value={userAddress} onChange={(event) => setUserAddress(event.target.value)} required placeholder={themeI18n('store_locator.address_placeholder')}/>

    <button className='locations-drawer__search-submit' type="submit" aria-label={themeI18n('store_locator.address_submit')}>
      <Icons icon='search' />
    </button>
  </form>
)
